import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { invoicingService } from '@/api/invoicing'
import { expensesService, type Expense } from '@/api/expenses'
import { toast } from '@/hooks/use-toast'
import { handleServerError } from '@/utils/handle-server-error'

const fmtMoney = (v: string) => {
  const num = parseFloat(v)
  if (isNaN(num)) return v
  return new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(num)
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  expense: Expense | null
  onBilled: () => void
}

const BILLABLE_STATUSES = ['draft', 'sent', 'partial']

export function BillExpenseDialog({ open, onOpenChange, expense, onBilled }: Props) {
  const { t } = useTranslation()
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('')
  const [saving, setSaving] = useState(false)

  const caseId = expense?.case ?? null

  const { data: invoicesData, isLoading } = useQuery({
    queryKey: ['case-invoices-for-bill', caseId],
    queryFn: () => invoicingService.listByCase(caseId!, { page_size: 50 }),
    enabled: open && !!caseId,
  })

  const invoices = (invoicesData?.results ?? []).filter((inv) =>
    BILLABLE_STATUSES.includes(inv.status),
  )

  const handleBill = async () => {
    if (!expense || !selectedInvoiceId) return
    setSaving(true)
    try {
      await expensesService.bill(expense.id, selectedInvoiceId)
      toast({ title: t('exp_bill_success') })
      onBilled()
      onOpenChange(false)
      setSelectedInvoiceId('')
    } catch (err) {
      handleServerError(err)
    } finally {
      setSaving(false)
    }
  }

  const handleClose = (o: boolean) => {
    if (!o) setSelectedInvoiceId('')
    onOpenChange(o)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('exp_bill_dialog_title')}</DialogTitle>
          <DialogDescription>
            {expense && (
              <span dir="auto">
                {expense.description} — {fmtMoney(expense.amount)}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {isLoading && (
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          )}
          {!isLoading && invoices.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('exp_bill_no_invoices')}</p>
          )}
          {invoices.length > 0 && (
            <RadioGroup value={selectedInvoiceId} onValueChange={setSelectedInvoiceId}>
              {invoices.map((inv) => (
                <div key={inv.id} className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value={inv.id} id={`inv-${inv.id}`} />
                  <Label htmlFor={`inv-${inv.id}`} className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm">#{inv.number}</span>
                      <Badge variant="outline" className="text-xs">
                        {t(`inv_status_${inv.status}`)}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground">{inv.client_name}</span>
                      <span className="text-xs font-medium">{fmtMoney(inv.total_ttc)}</span>
                    </div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleBill}
            disabled={!selectedInvoiceId || saving}
          >
            {saving ? t('common.saving') : t('exp_bill_confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
